import { App, NavControllerBase, Platform, ToastController } from 'ionic-angular';
import { AppLoadingServiceProvider } from '../app-loading-service/AppLoadingServiceProvider';
import { CalendarServiceProvider } from '../../../calendar/services/calendar-service/calendar-service';
import { ConfigServiceProvider } from '../config-service/config-service';
import { EventsServiceProvider } from '../../../calendar/services/events-service/events-service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Push, PushObject, PushOptions } from '@ionic-native/push';
import { skipWhile, take } from 'rxjs/operators';
import { StorageServiceProvider } from '../storage-service/storage-service';

const options: PushOptions = {
  android: {
    senderID: '435712664936',
    vibrate: true,
    icon: 'push_icon.png',
    forceShow: true,
  },
  ios: {
    alert: 'true',
    badge: 'true',
    sound: 'true',
  },
};

@Injectable()
export class PushServiceProvider {
  welcomePage: string = '';
  pushObject: PushObject;
  deviceToken;
  deviceRequestParams = {
    headers: {
      'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
    },
  };
  client;
  event;
  background = false;
  toast;
  private notificationId: string = '';

  constructor(
    private app: App,
    private configService: ConfigServiceProvider,
    private push: Push,
    private platform: Platform,
    private storage: StorageServiceProvider,
    private toastCtrl: ToastController,
    public http: HttpClient,
    private calendarService: CalendarServiceProvider,
    private eventsService: EventsServiceProvider,
    private appLoadingServ: AppLoadingServiceProvider,
  ) {}

  registerPushNotifications() {
    this.pushObject = this.push.init(options);

    this.pushObject.on('registration').subscribe((token) => {
      this.deviceToken = token.registrationId;
      this.registerDevice();
    });

    this.pushObject.on('notification').subscribe((notification: Notification) => {
      if (notification.additionalData.screen_name === 'TaskDetailsPage') {
        this.processTaskDetails(notification);
      } else if (notification.additionalData.screen_name === 'ProjectDetailsPage') {
        this.processProjectDetails(notification);
      } else if (notification.additionalData.type === 'news') {
        this.processNews(notification);
      } else if (notification.additionalData.type === 'location') {
        this.processLocation(notification);
      } else if (notification.additionalData.type === 'EVENT') {
        this.processEvent(notification);
      }
    });

    this.pushObject.on('error').subscribe((error) => console.error('Error with Push plugin', error));
  }

  registerDevice() {
    if (this.platform.is('ios') || this.platform.is('android')) {
      let deviceType = this.platform.is('ios') ? 1 : 2;
      let deviceRequestData = new URLSearchParams();
      deviceRequestData.append('token', this.deviceToken);
      deviceRequestData.append('platform', deviceType.toString());
      this.http
        .post(this.configService.getUrl('registerDevice'), deviceRequestData.toString(), this.deviceRequestParams)
        .toPromise()
        .then((response) => {
          console.log(response);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }

  unregisterPushNotifications() {
    return new Promise<void>((resolve, reject) => {
      if (this.pushObject && this.pushObject.unregister()) {
        this.pushObject
          .unregister()
          .then(() => {
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      } else {
        reject();
      }
    });
  }

  showToast(message: string) {
    this.toast = this.toastCtrl
      .create({
        message: message,
        duration: 3000,
        position: 'top',
      })
      .present();
  }

  private processLocation(notification: Notification) {
    let client = this.client.find((client) => {
      return client.id == notification.additionalData.client;
    });

    let event;
    if (client.nextEvent && client.nextEvent.id == notification.additionalData.event) {
      event = client.nextEvent;
    } else if (client.prevEvent && client.prevEvent.id == notification.additionalData.event) {
      event = client.prevEvent;
    } else {
      return;
    }

    this.app
      .getActiveNav()
      .setPages([
        'MenuPage',
        [
          'ClientPage',
          {
            client: notification.additionalData.client,
          },
        ],
        [
          'EventPage',
          {
            event: notification.additionalData.event,
            client: notification.additionalData.client,
            calendar: true,
          },
        ],
      ])
      .then();
  }

  public processTaskDetails(notification: Notification) {
    const taskId = notification && notification.additionalData && notification.additionalData.content;
    const isInForeground = !notification.additionalData.foreground;
    const hasNewId = this.notificationId !== notification.additionalData.id;

    if (taskId && isInForeground && hasNewId) {
      this.notificationId = notification.additionalData.id;
      this.appLoadingServ.menuLoaded
        .pipe(
          skipWhile((val) => val !== true),
          take(1),
        )
        .subscribe(() => {
          (this.app.getNavByIdOrName('menuNav') as NavControllerBase).setRoot('TaskListPage', { pushTaskId: taskId });
        });
    }
  }

  public processProjectDetails(notification: Notification) {
    const projectId = notification && notification.additionalData && notification.additionalData.content;
    const isInForeground = !notification.additionalData.foreground;
    const hasNewId = this.notificationId !== notification.additionalData.id;

    if (projectId && isInForeground && hasNewId) {
      this.notificationId = notification.additionalData.id;
      this.appLoadingServ.menuLoaded
        .pipe(
          skipWhile((val) => val !== true),
          take(1),
        )
        .subscribe(() => {
          (this.app.getNavByIdOrName('menuNav') as NavControllerBase).setRoot('ProjectsPage', { pushProjectId: projectId });
        });
    }
  }

  private processNews(notification) {
    if (!notification.additionalData.foreground) {
      this.app.getActiveNav().setPages(['MenuPage', 'NewsPage']).then();
    } else {
      this.toast = this.toastCtrl
        .create({
          message: 'Nowa aktualność',
          duration: 3000,
          position: 'top',
          cssClass: 'toast-message',
        })
        .present();
    }

    if (notification.additionalData.coldstart) {
      this.storage.setObject('startupPages', ['MenuPage', 'NewsPage']);
    }
  }

  private processEvent(notification) {
    const eventId = notification && notification.additionalData && notification.additionalData.content ? +notification.additionalData.content : null;
    const isInForeground = !notification.additionalData.foreground;
    const hasNewId = this.notificationId !== notification.additionalData.id;

    if (eventId && isInForeground && hasNewId) {
      this.notificationId = notification.additionalData.id;
      this.appLoadingServ.appLoaded
        .pipe(
          skipWhile((val) => val !== true),
          take(1),
        )
        .subscribe(() => {
          this.calendarService.showEventDetail(this.eventsService.getEventById(eventId));
        });
    }
  }
}

export interface Notification {
  message: string;
  title?: string;
  additionalData: NotificationAdditionalData;
}

export interface NotificationAdditionalData {
  coldstart: boolean;
  content: string;
  'content-available': string;
  foreground: boolean;
  id: string;
  type: string;
  screen_name: string;
  client?: any;
  event?: any;
}



// WEBPACK FOOTER //
// ./src/modules/core/services/push-service/push-service.ts